import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import { GridFilterModel, GridFilterItem } from '@mui/x-data-grid'

// Interface for table registration
export interface TableRegistration {
  tableId: string
  columns: string[]
  onFilterChange: (filter: GridFilterModel) => void
  onFilteredDataChange?: (filteredData: any[]) => void
}

// Interface for global filter state
export interface GlobalFilterState {
  // Column-specific filters that apply to tables with matching columns
  columnFilters: Record<string, GridFilterItem>
  // Quick filter that applies to all tables
  quickFilter: string
  // Registered tables
  registeredTables: Record<string, TableRegistration>
}

// Interface for global filter context
export interface GlobalFilterContextValue {
  state: GlobalFilterState
  registerTable: (registration: TableRegistration) => void
  unregisterTable: (tableId: string) => void
  setColumnFilter: (field: string, filterItem: GridFilterItem | null) => void
  setQuickFilter: (value: string) => void
  clearAllFilters: () => void
  updateFilteredData: (tableId: string, filteredData: any[]) => void
  getFilteredData: (tableId: string) => any[]
  manuallyFilterData: (tableId: string, originalData: any[]) => any[]
}

// Create context
const GlobalFilterContext = createContext<GlobalFilterContextValue | null>(null)

// Hook to use global filter context
export const useGlobalFilterContext = (): GlobalFilterContextValue => {
  const context = useContext(GlobalFilterContext)
  if (!context) {
    throw new Error('useGlobalFilterContext must be used within a GlobalFilterProvider')
  }
  return context
}

// Provider component
export const GlobalFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<GlobalFilterState>({
    columnFilters: {},
    quickFilter: '',
    registeredTables: {},
  })

  // Store filtered data for each table
  const [filteredDataMap, setFilteredDataMap] = useState<Record<string, any[]>>({})

  const registerTable = useCallback((registration: TableRegistration) => {
    setState(prev => ({
      ...prev,
      registeredTables: {
        ...prev.registeredTables,
        [registration.tableId]: registration,
      },
    }))
  }, [])

  const unregisterTable = useCallback((tableId: string) => {
    setState(prev => {
      const { [tableId]: removed, ...remaining } = prev.registeredTables
      return {
        ...prev,
        registeredTables: remaining,
      }
    })
    
    setFilteredDataMap(prev => {
      const { [tableId]: removed, ...remaining } = prev
      return remaining
    })
  }, [])

  const setColumnFilter = useCallback((field: string, filterItem: GridFilterItem | null) => {
    setState(prev => {
      const newColumnFilters = { ...prev.columnFilters }
      
      if (filterItem) {
        newColumnFilters[field] = filterItem
      } else {
        delete newColumnFilters[field]
      }

      return {
        ...prev,
        columnFilters: newColumnFilters,
      }
    })
  }, [])

  const setQuickFilter = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      quickFilter: value,
    }))
  }, [])

  const clearAllFilters = useCallback(() => {
    setState(prev => ({
      ...prev,
      columnFilters: {},
      quickFilter: '',
    }))
  }, [])

  const updateFilteredData = useCallback((tableId: string, filteredData: any[]) => {
    setFilteredDataMap(prev => ({
      ...prev,
      [tableId]: filteredData,
    }))
  }, [])

  const getFilteredData = useCallback((tableId: string): any[] => {
    return filteredDataMap[tableId] || []
  }, [filteredDataMap])

  // Manual filtering function that applies global filters to any data set
  const manuallyFilterData = useCallback((tableId: string, originalData: any[]): any[] => {
    // Early return if no data
    if (!originalData || originalData.length === 0) {
      return originalData
    }
    
    let filtered = [...originalData]
    
    // Get registered table info
    const tableInfo = state.registeredTables[tableId]
    if (!tableInfo) {
      return filtered
    }
    
    // Apply column filters
    const applicableFilters = tableInfo.columns
      .map(column => state.columnFilters[column])
      .filter(Boolean)
    
    if (applicableFilters.length > 0) {
      filtered = filtered.filter(row => {
        return applicableFilters.every(filterItem => {
          if (!filterItem.field || !filterItem.operator || filterItem.value === undefined) {
            return true
          }
          
          const cellValue = row[filterItem.field]
          const filterValue = filterItem.value
          
          switch (filterItem.operator) {
            case 'contains':
              return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase())
            case 'equals':
              return cellValue === filterValue
            case 'startsWith':
              return String(cellValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
            case 'endsWith':
              return String(cellValue).toLowerCase().endsWith(String(filterValue).toLowerCase())
            case 'isEmpty':
              return cellValue === '' || cellValue === null || cellValue === undefined
            case 'isNotEmpty':
              return cellValue !== '' && cellValue !== null && cellValue !== undefined
            case '>':
              return Number(cellValue) > Number(filterValue)
            case '>=':
              return Number(cellValue) >= Number(filterValue)
            case '<':
              return Number(cellValue) < Number(filterValue)
            case '<=':
              return Number(cellValue) <= Number(filterValue)
            default:
              return true
          }
        })
      })
    }
    
    // Apply quick filter
    if (state.quickFilter) {
      const quickFilterValue = state.quickFilter.toLowerCase()
      filtered = filtered.filter(row => {
        return Object.values(row).some(value => 
          String(value).toLowerCase().includes(quickFilterValue)
        )
      })
    }
    
    return filtered
  }, [state.columnFilters, state.quickFilter, state.registeredTables])

  // Apply global filters to registered tables
  useEffect(() => {
    console.log('🌐 GLOBAL FILTER SYNC: Applying to registered tables:', Object.keys(state.registeredTables))
    
    Object.entries(state.registeredTables).forEach(([tableId, registration]) => {
      // Build filter model for this table based on its columns
      const applicableFilters = registration.columns
        .map(column => state.columnFilters[column])
        .filter(Boolean)

      // Create filter model with applicable column filters
      const filterModel: GridFilterModel = {
        items: applicableFilters,
        quickFilterValues: state.quickFilter ? [state.quickFilter] : undefined,
      }

      console.log(`🔄 Applying filter to ${tableId}:`, filterModel)
      
      // Apply the filter to the table (for UI consistency)
      registration.onFilterChange(filterModel)
    })
  }, [state.columnFilters, state.quickFilter, state.registeredTables])

  const contextValue = useMemo(() => ({
    state,
    registerTable,
    unregisterTable,
    setColumnFilter,
    setQuickFilter,
    clearAllFilters,
    updateFilteredData,
    getFilteredData,
    manuallyFilterData,
  }), [
    state,
    registerTable,
    unregisterTable,
    setColumnFilter,
    setQuickFilter,
    clearAllFilters,
    updateFilteredData,
    getFilteredData,
    manuallyFilterData,
  ])

  return (
    <GlobalFilterContext.Provider value={contextValue}>
      {children}
    </GlobalFilterContext.Provider>
  )
}

// Hook for tables to sync with global filters
export const useGlobalFilterSync = (
  tableId: string,
  columns: string[],
  onFilterChange: (filter: GridFilterModel) => void,
  onFilteredDataChange?: (filteredData: any[]) => void
) => {
  const { registerTable, unregisterTable, setColumnFilter, setQuickFilter } = useGlobalFilterContext()

  // Register table on mount
  useEffect(() => {
    const registration: TableRegistration = {
      tableId,
      columns,
      onFilterChange,
      onFilteredDataChange,
    }
    
    registerTable(registration)

    return () => {
      unregisterTable(tableId)
    }
  }, [tableId, columns, onFilterChange, onFilteredDataChange, registerTable, unregisterTable])

  // Return functions to update global filters from this table
  const updateGlobalColumnFilter = useCallback((field: string, filterItem: GridFilterItem | null) => {
    setColumnFilter(field, filterItem)
  }, [setColumnFilter])

  const updateGlobalQuickFilter = useCallback((value: string) => {
    setQuickFilter(value)
  }, [setQuickFilter])

  return {
    updateGlobalColumnFilter,
    updateGlobalQuickFilter,
  }
}

export default GlobalFilterProvider